/** Minimal valid shapes for a step an author adds or re-types (E3, spec
 * 2026-09-18 §6 Edit). Pure — shared by AddStepDialog and the document-mode
 * step editor. `ALLOWED_LEVELS` mirrors
 * backend/app/schemas/process_workflow.py — move both together. */
import type {
  ProcessExecutorJson,
  ProcessExitJson,
  ProcessLevel,
  ProcessStepJson,
  ProcessWorkflowDefinitionJson,
} from '@/types/workflowDefinition'

export type ExecutorType = ProcessExecutorJson['type']

export const EXECUTOR_TYPES: { value: ExecutorType; label: string }[] = [
  { value: 'person', label: 'A person' },
  { value: 'ai', label: 'AI, reviewed by a person' },
  { value: 'automation', label: 'An automation' },
  { value: 'conversation', label: 'A conversation' },
  { value: 'wait', label: 'Waits for something' },
]

export const ALLOWED_LEVELS: Record<ExecutorType, ProcessLevel[]> = {
  person: ['manual'],
  ai: ['manual', 'assisted', 'automatic'],
  automation: ['automatic'],
  conversation: [],
  wait: [],
}

/** The types a step may take. The server accepts a conversation executor
 * on the ENTRY step only (`executor_not_available`; E9 lifts this), so it is
 * offered there — and on a step that already is one, so the select can show
 * what the document says. */
export function executorTypesFor(isEntry: boolean, current?: ExecutorType) {
  return EXECUTOR_TYPES.filter((t) => t.value !== 'conversation' || isEntry || current === 'conversation')
}

/** Step, seat, field, lane and exit ids (`_KEY_RE` on the server). */
export const KEY_PATTERN = /^[a-z][a-z0-9_]*$/

/** The executor block for `type`, keeping the seat the step already had. */
export function executorSkeleton(
  type: ExecutorType,
  doc: ProcessWorkflowDefinitionJson,
  previous?: ProcessExecutorJson,
): ProcessExecutorJson {
  const previousSeat = previous && 'seat' in previous ? previous.seat : null
  const seat = previousSeat ?? doc.fallback_seat ?? Object.keys(doc.seats)[0] ?? ''
  const languages = doc.languages?.length ? doc.languages : ['en']
  switch (type) {
    case 'person':
      return { type, seat }
    case 'ai':
      return {
        type,
        seat,
        instruction: Object.fromEntries(languages.map((l) => [l, ''])),
        attachments: false,
        threshold: 0.8,
      }
    case 'automation':
      return { type, capability: '' }
    case 'conversation':
      return { type, agenda: doc.record.agenda, party: 'staff' }
    case 'wait':
      return { type, on: { event: '' }, ...(previousSeat ? { seat: previousSeat } : {}) }
  }
}

/** The `level` block a type takes: its most conservative level as the
 * default, only that one offered. Types with no levels carry none. */
export function levelSkeleton(type: ExecutorType): ProcessStepJson['level'] | undefined {
  const allowed = ALLOWED_LEVELS[type]
  return allowed.length > 0 ? { default: allowed[0], offered: [allowed[0]] } : undefined
}

/** Where a new exit points until the author rewires it: the next step in
 * reading order, else the first close outcome. */
export function defaultExit(doc: ProcessWorkflowDefinitionJson, afterStepId?: string): ProcessExitJson {
  const ids = Object.keys(doc.steps)
  const next = afterStepId ? ids[ids.indexOf(afterStepId) + 1] : undefined
  if (next) return { to: next }
  const outcome = doc.record.outcomes[0]
  return outcome ? { close: outcome } : { to: ids[0] ?? '' }
}

/** An automation step must wire a `failed` exit (`process_exit_incomplete`).
 * Until the author rewires it, a failure stays on the step and tells the
 * fallback seat. */
export function failedExit(doc: ProcessWorkflowDefinitionJson): ProcessExitJson {
  return { notify: doc.fallback_seat ?? Object.keys(doc.seats)[0] ?? '' }
}

/** A time wait must wire an `elapsed` exit (same finding). It leaves the way
 * the step's first exit does until the author rewires it. */
export function elapsedExit(doc: ProcessWorkflowDefinitionJson, stepId: string): ProcessExitJson {
  const first = Object.values(doc.steps[stepId]?.exits ?? {})[0]
  if (first == null) return defaultExit(doc, stepId)
  const { requires: _drop, ...target } = typeof first === 'string' ? { to: first } : first
  return target
}

export function stepSkeleton(
  doc: ProcessWorkflowDefinitionJson,
  spec: { title: string; lane: string; type: ExecutorType; seat?: string; afterId?: string },
): ProcessStepJson {
  const languages = doc.languages?.length ? doc.languages : ['en']
  let executor = executorSkeleton(spec.type, doc)
  // A wait's seat is optional, so its skeleton carries none to overwrite.
  if (spec.seat && (executor.type === 'person' || executor.type === 'ai' || executor.type === 'wait')) {
    executor = { ...executor, seat: spec.seat }
  }
  const level = levelSkeleton(spec.type)
  return {
    title: { [languages[0]]: spec.title },
    lane: spec.lane,
    needs: [],
    produces: [],
    executor,
    ...(level ? { level } : {}),
    exits: {
      done: defaultExit(doc, spec.afterId),
      ...(spec.type === 'automation' ? { failed: failedExit(doc) } : {}),
    },
  }
}
